/**
 * The §A3 error envelope, as the one exception type the UI catches.
 *
 * Every non-2xx response from any handler is `{"error": {code, message, details, request_id}}` (§A3). `ApiError`
 * carries all four, and `request_id` is what §B7.4 puts next to every error toast.
 */

/** §A3.1's `ERROR_CODES`, verbatim. A code outside this set still reaches the UI — it is rendered as-is. */
export const ERROR_CODES = [
  "unauthorized",
  "csrf_failed",
  "forbidden",
  "not_found",
  "validation_failed",
  "illegal_transition",
  "scope_frozen",
  "blocking_reviews_remain",
  "package_changed",
  "already_resolved",
  "connection_in_use",
  "migration_busy",
  "not_in_state",
  "idempotency_mismatch",
  "oauth_state_invalid",
  "oauth_denied",
  "payload_too_large",
  "gone",
  "job_defer_failed",
  "rate_limited",
  "internal",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(args: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, unknown>;
    requestId?: string | null;
  }) {
    super(args.message);
    this.name = "ApiError";
    this.code = args.code;
    this.status = args.status;
    this.details = args.details ?? {};
    this.requestId = args.requestId ?? null;
  }

  is(code: string): boolean {
    return this.code === code;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

/**
 * Map an error body to an `ApiError`. A body that is not the §A3 envelope (a proxy's HTML 502, an empty 401)
 * becomes `internal`/`unauthorized` with a generic message rather than `undefined` on screen.
 */
export function toApiError(status: number, body: unknown, requestId: string | null): ApiError {
  const envelope = asRecord(asRecord(body).error);
  const code = typeof envelope.code === "string" ? envelope.code : fallbackCode(status);
  const message = typeof envelope.message === "string" ? envelope.message : fallbackCode(status);
  return new ApiError({
    code,
    message,
    status,
    details: asRecord(envelope.details),
    requestId: typeof envelope.request_id === "string" ? envelope.request_id : requestId,
  });
}

/**
 * A safe, user-facing summary for a caught error, for the one banner every screen renders it in.
 *
 * Every code OTHER than `validation_failed` renders `caught.message` unchanged - those are hand-written,
 * already safe sentences (§A3.1). `validation_failed` is different: `api/errors.py::_validation` always
 * sends the SAME generic sentence ("the request body or query did not validate") for every pydantic
 * failure, because the sentence cannot describe an arbitrary field without either being wrong for most of
 * them or leaking the field's VALUE - so the real detail travels out-of-band, as `details.fields` (each
 * item already dropped `input`/`ctx`/`url` server-side, in `_fields` - never a value, only a field path
 * and a message). This turns that array back into a one-line, still value-free summary, so the operator
 * sees WHICH field failed and why instead of a sentence that fits every 422 identically.
 *
 * Falls back to the generic message whenever `details.fields` is missing, empty or not shaped as
 * expected - a caller with no fields to summarise is not a bug in this function.
 */
export function describeApiError(caught: unknown, fallback: string): string {
  if (!isApiError(caught)) return fallback;
  if (caught.code !== "validation_failed") return caught.message;
  const fields = caught.details.fields;
  if (!Array.isArray(fields)) return caught.message;
  const parts = fields
    .map((entry) => {
      const rec = asRecord(entry);
      const loc = Array.isArray(rec.loc)
        ? rec.loc.filter((p): p is string => typeof p === "string").join(".")
        : "";
      const msg = typeof rec.msg === "string" ? rec.msg : "";
      if (loc && msg) return `${loc}: ${msg}`;
      return msg || loc || null;
    })
    .filter((part): part is string => part !== null && part !== "");
  return parts.length > 0 ? parts.join("; ") : caught.message;
}

function fallbackCode(status: number): string {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 410) return "gone";
  if (status === 413) return "payload_too_large";
  if (status === 429) return "rate_limited";
  return "internal";
}
